import type { RadioGroupProps } from '@wix/design-system';
import {
  Box,
  Divider,
  FormField,
  InfoIcon,
  RadioGroup as RadioGroupBase,
  Text,
} from '@wix/design-system';
import React, { useContext } from 'react';

import { RicosContext, ZIndexContext } from './../../../ricos-context';
import type {
  PollData_Poll_Settings,
  PollData_Poll_Settings_Permissions,
} from './../../../ricos-schema/next-schema';
import {
  PollData_Poll_Settings_Permissions_ViewRole,
  PollData_Poll_Settings_Permissions_VoteRole,
} from './../../../ricos-schema/next-schema';
import type { LogBiFn } from './../../../_shared/bi-context-provider/bi-context-provider';
import { logInvariant } from './../../../_shared/log-invariant';
import { useNodeAttrs } from './../../../_shared/use-node-attrs';
import { ToggleButton, usePanelContainerElement } from './../../../ui-components';

import {
  GENERAL_SETTINGS_EACH_PERSON_VOTE_TOGGLE,
  GENERAL_SETTINGS_MULTIPLE_ANSWERS_TOGGLE,
  GENERAL_SETTINGS_VIEW_PERMISSIONS_RADIO_GROUP,
  GENERAL_SETTINGS_VOTE_COUNT_TOGGLE,
  GENERAL_SETTINGS_VOTE_PERMISSIONS_RADIO_GROUP,
} from '../../data-hooks';
import type { PollPluginEditorConfig } from '../../types';
import { Section } from './section';

type PollSettingsSectionProps = {
  nodeId: string;
  config: PollPluginEditorConfig;
  logBi: LogBiFn;
};

export const PollSettingsSection: React.FC<PollSettingsSectionProps> = ({
  nodeId,
  config,
  logBi,
}) => {
  const { t } = useContext(RicosContext);

  const { pmNodeAttrs, patchPmNodeAttrs } = useNodeAttrs(nodeId, 'POLL');

  if (!pmNodeAttrs) {
    logInvariant(Error('Poll settings: node attrs not found'));
    return null;
  }

  const updatePermissions = (permissions: PollData_Poll_Settings_Permissions) => {
    patchPmNodeAttrs({
      poll: {
        options: [],
        ...pmNodeAttrs.poll,
        settings: {
          ...pmNodeAttrs.poll?.settings,
          permissions: {
            ...pmNodeAttrs.poll?.settings?.permissions,
            ...permissions,
          },
        },
      },
    });
  };

  const updateSettings = (settings: PollData_Poll_Settings) => {
    patchPmNodeAttrs({
      poll: {
        options: [],
        ...pmNodeAttrs.poll,
        settings: {
          ...pmNodeAttrs?.poll?.settings,
          ...settings,
        },
      },
    });
  };

  const pollPermissions = pmNodeAttrs.poll?.settings?.permissions;

  return (
    <>
      <Section title={t('Poll_PollSettings_Tab_Settings_Section_Voting_Header')}>
        {config.showVoteRoleSetting && (
          <RadioGroup
            label={t('Poll_PollSettings_Tab_Settings_Section_Voting_Permission_Title')}
            value={pollPermissions?.vote}
            onChange={vote => {
              logBi({ dataHook: GENERAL_SETTINGS_VOTE_PERMISSIONS_RADIO_GROUP, value: vote });
              updatePermissions({ vote: vote as PollData_Poll_Settings_Permissions_VoteRole });
            }}
            options={[
              {
                value: PollData_Poll_Settings_Permissions_VoteRole.SITE_MEMBERS,
                label: (
                  <Text size="small">
                    {t('Poll_PollSettings_Tab_Settings_Section_Voting_Permission_SiteMember')}
                  </Text>
                ),
              },
              {
                value: PollData_Poll_Settings_Permissions_VoteRole.ALL,
                label: (
                  <Text size="small">
                    {t('Poll_PollSettings_Tab_Settings_Section_Voting_Permission_All')}
                  </Text>
                ),
              },
            ]}
            dataHook={GENERAL_SETTINGS_VOTE_PERMISSIONS_RADIO_GROUP}
          />
        )}
        <ToggleButton
          dataHook={GENERAL_SETTINGS_MULTIPLE_ANSWERS_TOGGLE}
          title={t('Poll_PollSettings_Tab_Settings_Section_Voting_Multiselect')}
          checked={!!pollPermissions?.allowMultipleVotes}
          onChange={() => {
            logBi({
              dataHook: GENERAL_SETTINGS_MULTIPLE_ANSWERS_TOGGLE,
              value: !pollPermissions?.allowMultipleVotes,
            });
            updatePermissions({ allowMultipleVotes: !pollPermissions?.allowMultipleVotes });
          }}
          size="small"
        />
      </Section>

      <Box marginInline="-18px">
        <Divider />
      </Box>

      <Section title={t('Poll_PollSettings_Tab_Settings_Section_Results_Header')}>
        <RadioGroup
          label={t('Poll_PollSettings_Tab_Settings_Section_Results_Permission_Title')}
          value={pollPermissions?.view}
          onChange={view => {
            logBi({
              dataHook: GENERAL_SETTINGS_VIEW_PERMISSIONS_RADIO_GROUP,
              value: view,
            });
            updatePermissions({ view: view as PollData_Poll_Settings_Permissions_ViewRole });
          }}
          options={[
            {
              value: PollData_Poll_Settings_Permissions_ViewRole.EVERYONE,
              label: (
                <TextWithInfo
                  text={t('Poll_PollSettings_Tab_Settings_Section_Results_Permission_All')}
                  infoContent={t(
                    'Poll_PollSettings_Tab_Settings_Section_Results_Permission_All_Tooltip'
                  )}
                />
              ),
            },
            {
              value: PollData_Poll_Settings_Permissions_ViewRole.VOTERS,
              label: (
                <TextWithInfo
                  text={t('Poll_PollSettings_Tab_Settings_Section_Results_Permission_Voters')}
                  infoContent={t(
                    'Poll_PollSettings_Tab_Settings_Section_Results_Permission_Voters_Tooltip'
                  )}
                />
              ),
            },
            {
              value: PollData_Poll_Settings_Permissions_ViewRole.CREATOR,
              label: (
                <TextWithInfo
                  text={t('Poll_PollSettings_Tab_Settings_Section_Results_Permission_Owner')}
                  infoContent={t(
                    'Poll_PollSettings_Tab_Settings_Section_Results_Permission_Owner_Tooltip'
                  )}
                />
              ),
            },
          ]}
          dataHook={GENERAL_SETTINGS_VIEW_PERMISSIONS_RADIO_GROUP}
        />
        {pollPermissions?.view !== PollData_Poll_Settings_Permissions_ViewRole.CREATOR && (
          <>
            <ToggleButton
              dataHook={GENERAL_SETTINGS_VOTE_COUNT_TOGGLE}
              title={t('Poll_PollSettings_Tab_Settings_Section_Results_VoteVisibility')}
              checked={!!pmNodeAttrs.poll?.settings?.showVotesCount}
              onChange={() => {
                logBi({
                  dataHook: GENERAL_SETTINGS_VOTE_COUNT_TOGGLE,
                  value: !pmNodeAttrs.poll?.settings?.showVotesCount,
                });
                updateSettings({
                  showVoters: !pmNodeAttrs.poll?.settings?.showVotesCount,
                  showVotesCount: !pmNodeAttrs.poll?.settings?.showVotesCount,
                });
              }}
              size="small"
            />
            {pmNodeAttrs.poll?.settings?.showVotesCount && (
              <ToggleButton
                dataHook={GENERAL_SETTINGS_EACH_PERSON_VOTE_TOGGLE}
                title={t('Poll_PollSettings_Tab_Settings_Section_Results_VoterAnonymous')}
                checked={!!pmNodeAttrs.poll?.settings?.showVoters}
                onChange={() => {
                  logBi({
                    dataHook: GENERAL_SETTINGS_EACH_PERSON_VOTE_TOGGLE,
                    value: !pmNodeAttrs.poll?.settings?.showVoters,
                  });
                  updateSettings({ showVoters: !pmNodeAttrs.poll?.settings?.showVoters });
                }}
                size="small"
              />
            )}
          </>
        )}
      </Section>
    </>
  );
};

const RadioGroup: React.FC<{
  label: string;
  value: string | undefined;
  onChange: (data: string) => void;
  options: { value: string; label: React.JSX.Element | string }[];
  dataHook: string;
}> = ({ label, value, onChange, options, dataHook }) => {
  return (
    <FormField label={label}>
      <RadioGroupBase
        value={value}
        onChange={onChange as RadioGroupProps['onChange']}
        size="small"
        dataHook={dataHook}
      >
        {options.map(({ value, label }) => (
          <RadioGroupBase.Radio key={value} value={value}>
            {label}
          </RadioGroupBase.Radio>
        ))}
      </RadioGroupBase>
    </FormField>
  );
};

const TextWithInfo: React.FC<{
  text: string;
  infoContent: string;
}> = ({ text, infoContent }) => {
  const zIndexService = useContext(ZIndexContext);
  const containerEl = usePanelContainerElement();

  return (
    <Box verticalAlign="middle" gap="SP1">
      <Text size="small">{text}</Text>
      <InfoIcon
        size="small"
        content={infoContent}
        tooltipProps={{
          appendTo: containerEl,
          zIndex: zIndexService.getZIndex('TOOLTIP'),
        }}
      />
    </Box>
  );
};
